import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Button, Drawer } from "components";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useDefaultCard } from "controller";

// @ts-ignore
import VisaSvg from "assets/images/visa.svg";
// @ts-ignore
import MastercardSvg from "assets/images/mastercard.svg";
// @ts-ignore
import DefaultCard from "assets/images/defaultCard.svg";

interface Card {
  id: string;
  card: {
    last4: string;
    brand: string;
    exp_month: number;
    exp_year: number;
  };
}

interface CardListDrawerProps {
  open: boolean;
  onClose: () => void;
  cardList: Card[] | undefined;
  currentSourceId: string | undefined;
  onCardUpdated: () => void;
}

export const CardListDrawer = memo(
  ({
    open,
    onClose,
    cardList,
    currentSourceId,
    onCardUpdated,
  }: CardListDrawerProps) => {
    const theme = useTheme();
    const [selectedPrimaryCardId, setSelectedPrimaryCardId] = useState<
      string | null
    >(null);
    const [settingDefaultCardId, setSettingDefaultCardId] = useState<
      string | null
    >(null);
    const { mutateAsync: setDefaultCard } = useDefaultCard();

    const cardIcon = useCallback((type: string) => {
      switch (type) {
        case "visa":
          return VisaSvg;
        case "mastercard":
          return MastercardSvg;
        default:
          return DefaultCard;
      }
    }, []);

    useEffect(() => {
      if (open && currentSourceId) {
        setSelectedPrimaryCardId(currentSourceId);
      }
    }, [open, currentSourceId]);

    const handleSetAsDefault = useCallback(
      async (id: string) => {
        try {
          await setDefaultCard(id);
          onCardUpdated();
        } catch (error) {
          toast.error("Unable to set default card");
        }
      },
      [setDefaultCard, onCardUpdated],
    );

    const handleSaveCardChanges = useCallback(async () => {
      if (selectedPrimaryCardId && selectedPrimaryCardId !== currentSourceId) {
        await handleSetAsDefault(selectedPrimaryCardId);
        toast.success("Primary card updated successfully");
      }
      onClose();
    }, [selectedPrimaryCardId, currentSourceId, handleSetAsDefault, onClose]);

    const handleCancel = useCallback(() => {
      onClose();
    }, [onClose]);

    const handleSelectCard = useCallback(
      async (cardId: string) => {
        setSettingDefaultCardId(cardId);
        try {
          await setDefaultCard(cardId);
          setSelectedPrimaryCardId(cardId);
          onCardUpdated();
          toast.success("Primary card updated successfully");
        } catch (error) {
          toast.error("Unable to set default card");
        } finally {
          setSettingDefaultCardId(null);
        }
      },
      [setDefaultCard, onCardUpdated],
    );

    const footer = <></>;

    const dividerElement = useMemo(
      () => (
        <Divider
          sx={{
            borderStyle: "dashed",
            borderColor: theme.palette.neutral["015"],
          }}
          orientation="horizontal"
          flexItem
        />
      ),
      [theme.palette.neutral],
    );

    if (!open) return null;

    return (
      <Drawer open={open} title="Card list" onClose={onClose} footer={footer}>
        <Stack sx={{ width: "500px", py: "16px" }}>
          <Stack divider={dividerElement}>
            {cardList?.map((card) => {
              const isDefault =
                card.id === selectedPrimaryCardId || cardList.length === 1;
              return (
                <Stack
                  key={card.id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: "24px",
                    py: "16px",
                  }}
                >
                  <Stack direction="row" alignItems="center" gap="16px">
                    <Stack
                      sx={{
                        border: `1px solid ${theme.palette.neutral["015"]}`,
                        width: "48px",
                        height: "48px",
                        borderRadius: "50%",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: theme.palette.neutral.main,
                      }}
                    >
                      <Box
                        component="img"
                        src={cardIcon(card?.card?.brand)}
                        sx={{ width: "28px", height: "auto" }}
                      />
                    </Stack>
                    <Stack>
                      <Typography
                        variant="caption-2"
                        sx={{ textTransform: "capitalize" }}
                      >
                        {card?.card?.brand} ********{card?.card?.last4}
                      </Typography>
                      <Typography
                        variant="body-small"
                        color={theme.palette.lightBg.medium}
                        sx={{ textTransform: "capitalize" }}
                      >
                        Expires{" "}
                        {card?.card?.exp_month?.toString().padStart(2, "0")}/
                        {card?.card?.exp_year}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack direction="row" alignItems="center">
                    {isDefault ? (
                      <Chip
                        label="PRIMARY"
                        size="small"
                        sx={{
                          ...theme.typography["tag-label-xs"],
                          backgroundColor: theme.palette.secondary.main,
                          color: theme.palette.primitive.brand.purple[600],
                          fontWeight: 700,
                          borderRadius: "4px",
                          height: "24px",
                          textTransform: "uppercase",
                        }}
                      />
                    ) : (
                      <Button
                        variant="ghost"
                        size="small"
                        isLink
                        onClick={() => handleSelectCard(card.id)}
                        disabled={settingDefaultCardId === card.id}
                      >
                        Set as primary
                        {settingDefaultCardId === card.id && (
                          <CircularProgress
                            size={14}
                            sx={{ ml: 1, color: "inherit" }}
                          />
                        )}
                      </Button>
                    )}
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      </Drawer>
    );
  },
);

CardListDrawer.displayName = "CardListDrawer";
